import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type SavedSearchFilters = {
  query: string;
  location: string;
  jobType: string;
  returnerFriendly: boolean;
  remoteOnly: boolean;
  tags: string[];
  minPay: number | null;
  maxPay: number | null;
};

export type NotificationCadence = 'daily' | 'weekly' | 'off';

export type SavedSearch = SavedSearchFilters & {
  id: string;
  name: string;
  createdAt: string;
  cadence: NotificationCadence;
};

export type SavedSearchSettings = {
  defaultCadence: NotificationCadence;
};

export type AddSavedSearchInput = SavedSearchFilters & {
  name: string;
  cadence?: NotificationCadence;
};

const STORAGE_KEY = 'lighthouse_jobs_saved_searches';
const SETTINGS_KEY = 'lighthouse_jobs_saved_searches_settings';
const PUSH_PREF_KEY = 'lighthouse_jobs_saved_searches_push_enabled';

const normaliseSavedSearch = (search: Partial<SavedSearch>): SavedSearch | null => {
  if (!search) return null;
  return {
    id: search.id ?? nextId(),
    name: search.name ?? 'Saved search',
    createdAt: search.createdAt ?? new Date().toISOString(),
    cadence: (search.cadence ?? 'weekly') as NotificationCadence,
    query: search.query ?? '',
    location: search.location ?? '',
    jobType: search.jobType ?? 'all',
    returnerFriendly: Boolean(search.returnerFriendly),
    remoteOnly: Boolean(search.remoteOnly),
    tags: Array.isArray(search.tags) ? search.tags : [],
    minPay: typeof search.minPay === 'number' ? search.minPay : null,
    maxPay: typeof search.maxPay === 'number' ? search.maxPay : null,
  };
};

const readLocalSearches = (): SavedSearch[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<SavedSearch>[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((search) => normaliseSavedSearch(search))
      .filter((search): search is SavedSearch => Boolean(search));
  } catch (error) {
    console.warn('[saved-searches] Failed to read local data', error);
    return [];
  }
};

const writeLocalSearches = (searches: SavedSearch[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
  } catch (error) {
    console.warn('[saved-searches] Failed to persist local data', error);
  }
};

const readLocalSettings = (): SavedSearchSettings => {
  if (typeof window === 'undefined') return { defaultCadence: 'weekly' };
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { defaultCadence: 'weekly' };
    const parsed = JSON.parse(raw) as SavedSearchSettings;
    return parsed?.defaultCadence ? parsed : { defaultCadence: 'weekly' };
  } catch (error) {
    console.warn('[saved-searches] Failed to read local settings', error);
    return { defaultCadence: 'weekly' };
  }
};

const writeLocalSettings = (settings: SavedSearchSettings) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('[saved-searches] Failed to persist settings', error);
  }
};

export const readLocalPushPreference = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(PUSH_PREF_KEY);
    if (!raw) return false;
    return raw === 'true';
  } catch (error) {
    console.warn('[saved-searches] Failed to read push preference', error);
    return false;
  }
};

export const writeLocalPushPreference = (enabled: boolean) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PUSH_PREF_KEY, String(enabled));
  } catch (error) {
    console.warn('[saved-searches] Failed to persist push preference', error);
  }
};

const nextId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);

const normaliseSearchList = (searches: unknown): SavedSearch[] => {
  if (!Array.isArray(searches)) return [];
  return searches
    .map((search) => normaliseSavedSearch(search as Partial<SavedSearch>))
    .filter((search): search is SavedSearch => Boolean(search));
};

export async function fetchSavedSearches(): Promise<SavedSearch[]> {
  if (!API_BASE_URL) {
    return readLocalSearches();
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/saved-searches`);
    if (!response.ok) {
      throw new Error(`Saved searches fetch failed (${response.status})`);
    }
    const payload = await response.json();
    if (Array.isArray(payload?.data)) return normaliseSearchList(payload.data);
    if (Array.isArray(payload)) return normaliseSearchList(payload);
    return readLocalSearches();
  } catch (error) {
    console.warn('[saved-searches] Falling back to local data', error);
    return readLocalSearches();
  }
}

export async function createSavedSearch(
  input: AddSavedSearchInput & { cadence: NotificationCadence },
): Promise<SavedSearch> {
  if (!API_BASE_URL) {
    const newSearch: SavedSearch = {
      ...input,
      id: nextId(),
      createdAt: new Date().toISOString(),
      tags: input.tags ?? [],
      minPay: typeof input.minPay === 'number' ? input.minPay : null,
      maxPay: typeof input.maxPay === 'number' ? input.maxPay : null,
    };
    const searches = [newSearch, ...readLocalSearches()];
    writeLocalSearches(searches);
    return newSearch;
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/saved-searches`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Saved search create failed (${response.status}): ${text}`);
    }
    const payload = (await response.json()) as Partial<SavedSearch>;
    return normaliseSavedSearch(payload) ?? {
      ...input,
      id: nextId(),
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    console.warn('[saved-searches] API create failed, using local store', error);
    const newSearch: SavedSearch = {
      ...input,
      id: nextId(),
      createdAt: new Date().toISOString(),
      tags: input.tags ?? [],
      minPay: typeof input.minPay === 'number' ? input.minPay : null,
      maxPay: typeof input.maxPay === 'number' ? input.maxPay : null,
    };
    const searches = [newSearch, ...readLocalSearches()];
    writeLocalSearches(searches);
    return newSearch;
  }
}

export async function deleteSavedSearch(id: string) {
  if (!API_BASE_URL) {
    const searches = readLocalSearches().filter((search) => search.id !== id);
    writeLocalSearches(searches);
    return;
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/saved-searches/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Saved search delete failed (${response.status}): ${text}`);
  }
}

export async function updateSavedSearchCadence(id: string, cadence: NotificationCadence): Promise<SavedSearch> {
  if (!API_BASE_URL) {
    const searches = readLocalSearches();
    const updated = searches.map((search) => (search.id === id ? { ...search, cadence } : search));
    writeLocalSearches(updated);
    return updated.find((search) => search.id === id) ?? updated[0];
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/saved-searches/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cadence }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Saved search update failed (${response.status}): ${text}`);
  }
  const payload = (await response.json()) as Partial<SavedSearch>;
  const normalised = normaliseSavedSearch(payload);
  if (!normalised) {
    throw new Error('Saved search update payload missing');
  }
  return normalised;
}

export async function fetchSavedSearchSettings(): Promise<SavedSearchSettings> {
  if (!API_BASE_URL) {
    return readLocalSettings();
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/saved-searches/settings`);
    if (!response.ok) {
      throw new Error(`Saved search settings fetch failed (${response.status})`);
    }
    const payload = (await response.json()) as SavedSearchSettings;
    return payload ?? { defaultCadence: 'weekly' };
  } catch (error) {
    console.warn('[saved-searches] Falling back to local settings', error);
    return readLocalSettings();
  }
}

export async function updateSavedSearchSettings(
  settings: SavedSearchSettings,
): Promise<SavedSearchSettings> {
  if (!API_BASE_URL) {
    writeLocalSettings(settings);
    return settings;
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/saved-searches/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Saved search settings update failed (${response.status}): ${text}`);
  }
  const payload = (await response.json()) as SavedSearchSettings;
  return payload ?? settings;
}
